import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Flex, Heading, Text, Avatar, Skeleton, SkeletonCircle, VStack, HStack, Button } from "@chakra-ui/react";
import HeaderComponent from "src/pages/register/HeaderComponent";
import { ProfileService } from "src/rest/profileService";
import type { ProfileDto } from "src/types/Profile";

const UserProfileDetails: React.FC = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileDto | null>(null);

  const backTo = (location.state as any)?.backTo as ("Accepted" | "Rejected" | undefined);
  const backHref = backTo === "Rejected" ? "/admin?tab=rejected" : backTo === "Accepted" ? "/admin?tab=accepted" : "/admin";

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        if (!email) throw new Error("Missing email");
        const data = await ProfileService.getProfile(decodeURIComponent(email));
        setProfile(data);
      } catch (e: any) {
        setError(e?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, [email]);

  if (error) {
    return <Flex justify="center" align="center" minH="60vh"><Text color="red.500">{error}</Text></Flex>;
  }

  const fullName = profile?.personalDetails?.firstName || "";
  const dob = profile?.personalDetails?.dob || "—";
  const contactNo = profile?.communicationDetails?.mobileNumber || "—";
  const edu = profile?.businessDetails?.educationalQualification || "—";
  const occupation = profile?.businessDetails?.occupation || "—";
  const business = profile?.businessDetails?.business || "—";

  // Address
  const comm = (profile as any)?.communicationDetails || {};
  const currentAddress = comm?.currentAddress || {};
  const addressLine1 = (currentAddress?.addressLine1 || comm?.addressLine1) || "—";
  const addressLine2 = (currentAddress?.addressLine2 || comm?.addressLine2) || "";
  const city = (currentAddress?.villageCity || comm?.city) || "—";
  const state = (currentAddress?.state || comm?.state) || "—";
  const pinCode = (currentAddress?.pincode || comm?.pinCode) || "—";

  // Parents
  const fatherName = profile?.personalDetails?.fatherName ?? profile?.familyDetails?.fatherName ?? "—";
  const motherName = profile?.personalDetails?.motherName ?? profile?.familyDetails?.motherName ?? "—";

  const members = profile?.members || [];

  return (
    <Box height="100%" px="2rem">
      <HStack width="100%" mb="2rem" justifyContent="space-between">
        <HeaderComponent />
      </HStack>

      <Flex mt={2} justifyContent="center" width="100%">
        <VStack width={{ base: "100%", md: "50%" }} spacing={4}>
          {/* Org strip */}
          <Box width="100%" height="7rem" borderRadius="20px" background="#FFF0E0">
            <Flex justifyContent="space-between" alignItems="center" height="100%" px={3}>
              <Box borderRadius="10px" p={1} bg="white">
                {loading ? <SkeletonCircle size="12" /> : <Avatar size="sm" name="MKS" />}
              </Box>
              <Box flex={1} ml={4}>
                <Heading lineHeight="100%" color="#4A2C2A" fontWeight="500" fontSize="1.2rem" letterSpacing="-0.32px">Maurya Kushwaha Samaj, Thane</Heading>
              </Box>
            </Flex>
          </Box>

          {/* Avatar and name */}
          <Box bottom={12} position="relative">{loading ? <SkeletonCircle size="16" /> : <Avatar boxShadow="0px 14px 38px rgba(0, 0, 0, 0.1)" border="1px solid #4A2C2A" size="lg" name={fullName} />}</Box>
          <HStack justifyContent="center">{loading ? <Skeleton height="28px" width="160px" mt={-16} /> : <Heading mt={-16} variant="h4" color="#2B2D42">{fullName}</Heading>}</HStack>

          {/* Contact / DOB */}
          <HStack p={2} boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)" borderRadius="16px" border="1px solid rgba(108, 117, 125, 0.18)" px={8} justifyContent="space-between">
            <VStack align="start">
              <Text fontWeight="700" color="#1F0812">Contact No.</Text>
              {loading ? <Skeleton height="20px" width="120px" /> : <Text color="#6C757D">{contactNo}</Text>}
            </VStack>
            <VStack align="start">
              <Text fontWeight="700" color="#1F0812">D.O.B</Text>
              {loading ? <Skeleton height="20px" width="80px" /> : <Text color="#6C757D">{dob}</Text>}
            </VStack>
          </HStack>

          {/* Education/Occupation/Business */}
          <HStack p={6} alignItems="start" width="100%" boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)" background="#FFF" borderRadius="16px" border="1px solid rgba(108, 117, 125, 0.18)" mt={4}>
            <VStack width="100%" spacing={4} align="start">
              {["Educational Qualification", "Occupation", "Business Details"].map((label, idx) => (
                <HStack justifyContent="space-between" width="100%" key={idx}>
                  <Text fontWeight="bold" color="#6C757D">{label}:</Text>
                  {loading ? <Skeleton height="20px" width="160px" /> : <Text>{idx === 0 ? edu : idx === 1 ? occupation : business}</Text>}
                </HStack>
              ))}
            </VStack>
          </HStack>

          {/* Address */}
          <HStack p={6} alignItems="start" width="100%" boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)" background="#FFF" borderRadius="16px" border="1px solid rgba(108, 117, 125, 0.18)">
            <VStack width="100%" spacing={3} align="start">
              <Heading letterSpacing="-0.48px" color="#6C757D" size="sm">Address</Heading>
              {loading ? (<><Skeleton height="18px" width="60%" /><Skeleton height="18px" width="40%" /></>) : (
                <>
                  <Text>{addressLine1}</Text>
                  {addressLine2 && <Text>{addressLine2}</Text>}
                  <Text>{city}{city !== "—" ? ", " : ""}{state}{pinCode !== "—" ? ` - ${pinCode}` : ""}</Text>
                </>
              )}
            </VStack>
          </HStack>

          {/* Family details */}
          <HStack p={6} alignItems="start" width="100%" boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)" background="#FFF" borderRadius="16px" border="1px solid rgba(108, 117, 125, 0.18)">
            <VStack width="100%" spacing={4} align="start">
              <Heading letterSpacing="-0.48px" color="#6C757D" size="sm">Family Details</Heading>
              <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Father's Name:</Text>{loading ? <Skeleton height="18px" width="160px" /> : <Text>{fatherName}</Text>}</HStack>
              <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Mother's Name:</Text>{loading ? <Skeleton height="18px" width="160px" /> : <Text>{motherName}</Text>}</HStack>
              {loading ? (
                <VStack width="100%" spacing={2}><Skeleton height="20px" width="80%" /><Skeleton height="20px" width="70%" /></VStack>
              ) : (members && members.length > 0 ? (
                <VStack width="100%" spacing={3} align="start">
                  {members.map((m, idx) => (
                    <Box key={idx} width="100%" borderTop="1px solid rgba(108,117,125,0.18)" pt={3}>
                      <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Name:</Text><Text>{(m as any).memberName || "—"}</Text></HStack>
                      <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Relation:</Text><Text>{(m as any).relation || "—"}</Text></HStack>
                      <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Email:</Text><Text>{(m as any).email || "—"}</Text></HStack>
                      <HStack justifyContent="space-between" width="100%"><Text fontWeight="bold" color="#6C757D">Mobile:</Text><Text>{(m as any).mobileNo || "—"}</Text></HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (<Text color="#6C757D">No additional family members</Text>))}
            </VStack>
          </HStack>

          {/* Back */}
          <HStack width="100%" justifyContent="space-between" mt={2}>
            <Button onClick={() => navigate(backHref, { state: { fromApp: true } })}>&larr; Back</Button>
            <Box />
          </HStack>
        </VStack>
      </Flex>
    </Box>
  );
};

export default UserProfileDetails;
export {};
